import axios from 'axios'
import { getAuth } from '@/auth/auth'
import { validateMessageUsageMap } from '@/models/messageUsage.js'

// /api is internal fake endpoint which gets redirected in nginx
const API_BASE_URL = import.meta.env.VITE_BACKEND_ENDPOINT
const BACKEND_ENDPOINT = API_BASE_URL + '/prompt/chat'
const EXPLAIN_ENDPOINT = API_BASE_URL + '/prompt/explain'
const AUDIO_SPEECH_ENDPOINT = API_BASE_URL + '/audio/speech'
const ME_ENDPOINT = API_BASE_URL + '/me'
const MY_VOCABULARY_BANKS_ENDPOINT = API_BASE_URL + '/me/vocabulary-banks'
const VOCABULARY_CATALOG_ENDPOINT = API_BASE_URL + '/vocabulary-banks'
const REGISTER_ENDPOINT = API_BASE_URL + '/register'
const NUMBER_TRAINING_ENDPOINT = API_BASE_URL + '/number-training'

/**
 * Sends a user message to the chat service.
 *
 * @async
 * @function userPrompt
 * @param {string} message - The message to send
 * @param {Array<{role: "user" | "assistant", content: string}>} conversationHistory - Prior chat turns
 * @param {{
 *   knownWordsBankId?: number,
 *   knownKanjisBankId?: number | null,
 *   focusWordsBankId?: number,
 *   learningLanguage?: string,
 *   explanationLanguage?: string,
 * }} chatConfig - Runtime chat configuration from the UI
 * @param {{
 *   onDelta?: (delta: string) => void,
 * }} streamOptions - Optional stream callbacks
 * @returns {Promise<Object>} The completed chat response
 * @throws {Error} If the message sending fails
 */
export async function userPrompt(
  message,
  conversationHistory = [],
  chatConfig = {},
  streamOptions = {},
) {
  let backendResponse, answer, markedAnswer, unknownWords, explanations, focusWordsFound

  backendResponse = await _sendChatMessage(message, conversationHistory, chatConfig, streamOptions)
  answer = backendResponse.response.raw
  markedAnswer = backendResponse.response.marked
  unknownWords = backendResponse.unknownWords
  explanations = {}
  focusWordsFound = backendResponse.focusWordsFound ?? []

  return {
    answer: answer,
    markedAnswer: markedAnswer,
    stats: {
      unknown: unknownWords,
      percentUnknown: parseInt(backendResponse.percentUnknown),
      explanations: explanations,
      focusWordsFound: focusWordsFound,
    },
  }
}

export async function listVocabularyBanks() {
  const auth = getAuth()
  await auth.init()
  const response = await axios.get(MY_VOCABULARY_BANKS_ENDPOINT, {
    headers: await buildAuthHeaders(),
  })
  return response.data.items ?? []
}

export async function getMe() {
  const auth = getAuth()
  await auth.init()
  const response = await axios.get(ME_ENDPOINT, {
    headers: await buildAuthHeaders(),
  })
  return parseCurrentUser(response.data)
}

export async function generateSpeechAudio(text, language) {
  const auth = getAuth()
  await auth.init()
  const response = await axios.post(
    AUDIO_SPEECH_ENDPOINT,
    { text, language },
    {
      headers: await buildAuthHeaders(),
      responseType: 'blob',
    },
  )
  const contentType = normalizeAudioContentType(response.headers['content-type'])

  return {
    blob: new Blob([response.data], { type: contentType }),
    contentType,
    outputFormat: response.headers['x-audio-output-format'] ?? '',
  }
}

export async function getNumberTrainingConfig() {
  const response = await axios.get(`${NUMBER_TRAINING_ENDPOINT}/config`)
  return response.data
}

const base64ToBlob = (encoded, contentType) => {
  const bytes = atob(encoded)
  const data = new Uint8Array(bytes.length)
  for (let index = 0; index < bytes.length; index += 1) {
    data[index] = bytes.charCodeAt(index)
  }
  return new Blob([data], { type: contentType || 'audio/mpeg' })
}

export async function generateNumberTrainingExercise(config, { signal } = {}) {
  const response = await axios.post(
    `${NUMBER_TRAINING_ENDPOINT}/exercises`,
    {
      language: config.language,
      category: config.category,
      difficulty: config.difficulty,
    },
    { signal },
  )
  const payload = response.data
  const blob = base64ToBlob(payload.audioBase64, payload.audioContentType)
  const audioUrl = URL.createObjectURL(blob)

  return {
    ...payload,
    audioBase64: undefined,
    audioUrl,
    dispose: () => URL.revokeObjectURL(audioUrl),
  }
}

export function trackNumberTrainingEvent(event, config, attributes = {}) {
  return fetch(`${NUMBER_TRAINING_ENDPOINT}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event,
      language: config.language,
      category: config.category,
      difficulty: config.difficulty,
      ...attributes,
    }),
    keepalive: true,
  }).catch(() => null)
}

export async function explainPromptWord(payload) {
  const auth = getAuth()
  await auth.init()
  const response = await axios.post(
    EXPLAIN_ENDPOINT,
    {
      word: payload.word,
      statement: payload.statement,
      sentence: payload.sentence ?? null,
      learningLanguage: payload.learningLanguage,
      explanationLanguage: payload.explanationLanguage,
    },
    {
      headers: await buildAuthHeaders(),
    },
  )
  return response.data
}

export async function listVocabularyCatalog() {
  const response = await axios.get(VOCABULARY_CATALOG_ENDPOINT)
  return response.data.items ?? []
}

export async function registerUser(payload) {
  const response = await axios.post(REGISTER_ENDPOINT, {
    username: payload.username,
    password: payload.password,
    password_confirmation: payload.passwordConfirmation,
    registration_token: payload.registrationToken,
  })
  return response.data
}

export async function createVocabularyBank(payload) {
  const auth = getAuth()
  await auth.init()
  const response = await axios.post(
    MY_VOCABULARY_BANKS_ENDPOINT,
    {
      title: payload.title,
      language: payload.language,
      level: payload.level,
      topic: payload.topic,
      scope: payload.scope,
      words: payload.words,
    },
    {
      headers: await buildAuthHeaders(),
    },
  )
  return response.data
}

export async function importVocabularyBank(catalogBankId) {
  const auth = getAuth()
  await auth.init()
  const response = await axios.post(
    `${MY_VOCABULARY_BANKS_ENDPOINT}/import`,
    {
      catalogBankId,
    },
    {
      headers: await buildAuthHeaders(),
    },
  )
  return response.data
}

export async function updateVocabularyBank(vocabularyBankId, payload) {
  const auth = getAuth()
  await auth.init()
  const response = await axios.put(
    `${MY_VOCABULARY_BANKS_ENDPOINT}/${vocabularyBankId}`,
    {
      title: payload.title,
      words: payload.words,
    },
    {
      headers: await buildAuthHeaders(),
    },
  )
  return response.data
}

export async function addVocabularyBankWord(vocabularyBankId, word) {
  const auth = getAuth()
  await auth.init()
  const response = await axios.post(
    `${MY_VOCABULARY_BANKS_ENDPOINT}/${vocabularyBankId}/add-word`,
    {
      word,
    },
    {
      headers: await buildAuthHeaders(),
    },
  )
  return response.data
}

export async function deleteVocabularyBank(vocabularyBankId) {
  const auth = getAuth()
  await auth.init()
  await axios.delete(`${MY_VOCABULARY_BANKS_ENDPOINT}/${vocabularyBankId}`, {
    headers: await buildAuthHeaders(),
  })
}

async function buildAuthHeaders() {
  const auth = getAuth()
  const token = await auth.getAccessToken()

  if (!token) {
    throw new Error('Authentication is required for backend requests')
  }

  return {
    Authorization: `Bearer ${token}`,
  }
}

const normalizeAudioContentType = (contentType) => {
  const mediaType = String(contentType ?? '')
    .split(';')[0]
    .trim()
    .toLowerCase()

  return mediaType.startsWith('audio/') ? mediaType : 'audio/mpeg'
}

const parseCurrentUser = (payload) => {
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Invalid current user payload')
  }

  return {
    ...payload,
    messageUsage: validateMessageUsageMap(payload.messageUsage),
  }
}

const normalizeOptionalBankId = (value) => {
  if (value === '' || value == null) return null

  return value
}

const buildChatRequestBody = (message, conversationHistory, chatConfig) => ({
  userPrompt: message,
  conversationHistory: JSON.stringify(conversationHistory),
  learningLanguage: chatConfig.learningLanguage ?? '',
  explanationLanguage: chatConfig.explanationLanguage ?? '',
  knownWordsBankId: chatConfig.knownWordsBankId,
  knownKanjisBankId: normalizeOptionalBankId(chatConfig.knownKanjisBankId),
  focusWordsBankId: chatConfig.focusWordsBankId,
})

const parseChatStreamLine = (line) => {
  const trimmedLine = line.trim()
  if (!trimmedLine) return null

  try {
    return JSON.parse(trimmedLine)
  } catch (error) {
    throw new Error(`Invalid chat stream event: ${error.message}`)
  }
}

const handleChatStreamEvent = (event, streamOptions) => {
  if (!event) return null

  if (event.type === 'response.delta') {
    const delta = String(event.delta ?? '')
    if (delta) {
      streamOptions.onDelta?.(delta)
    }
    return null
  }

  if (event.type === 'response.completed') {
    return event
  }

  if (event.type === 'error') {
    throw new Error(event.detail || 'Chat stream failed')
  }

  throw new Error(`Unsupported chat stream event: ${event.type}`)
}

const readChatStream = async (response, streamOptions) => {
  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Chat stream is not readable')
  }

  const decoder = new TextDecoder()
  let buffer = ''
  let completedEvent = null

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        completedEvent =
          handleChatStreamEvent(parseChatStreamLine(line), streamOptions) ?? completedEvent
      }
    }

    buffer += decoder.decode()
    const finalEvent = handleChatStreamEvent(parseChatStreamLine(buffer), streamOptions)
    completedEvent = finalEvent ?? completedEvent
  } finally {
    reader.releaseLock()
  }

  if (!completedEvent) {
    throw new Error('Chat stream ended before completion')
  }

  return completedEvent
}

const _sendChatMessage = async (message, conversationHistory, chatConfig, streamOptions) => {
  try {
    const response = await fetch(BACKEND_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/x-ndjson',
        ...(await buildAuthHeaders()),
      },
      body: JSON.stringify(buildChatRequestBody(message, conversationHistory, chatConfig)),
    })

    if (!response.ok) {
      const error = new Error(`Chat request failed with status ${response.status}`)
      error.response = { status: response.status }
      throw error
    }

    return await readChatStream(response, streamOptions ?? {})
  } catch (error) {
    console.error('Error sending chat message:', error)
    throw error
  }
}
